export const MAX_FILE_MB = 100
export const LARGE_PDF_WARNING_PAGES = 80

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

export interface PageOrderItem {
  id: string
  originalIndex: number
}

export interface ReorderProgress {
  current: number
  total: number
}

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function reorderedPdfFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-novyj-poryadok.pdf`
}

export function buildInitialPageOrder(totalPages: number): PageOrderItem[] {
  return Array.from({ length: totalPages }, (_, index) => ({
    id: `page-${index + 1}`,
    originalIndex: index,
  }))
}

export function countMovedPages(order: PageOrderItem[]): number {
  return order.filter((page, index) => page.originalIndex !== index).length
}

export function isOriginalOrder(order: PageOrderItem[]): boolean {
  return countMovedPages(order) === 0
}

export async function readPdfPageCount(file: File): Promise<number> {
  const { PDFDocument } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes)
  return pdfDoc.getPageCount()
}

export async function reorderPdfPages(
  file: File,
  order: PageOrderItem[],
  onProgress?: (progress: ReorderProgress) => void,
): Promise<{ blob: Blob; totalPages: number; movedPages: number }> {
  const { PDFDocument } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const sourcePdf = await PDFDocument.load(bytes)
  const totalPages = sourcePdf.getPageCount()

  if (order.length !== totalPages) {
    throw new Error('page_order_mismatch')
  }

  const seen = new Set<number>()
  for (const page of order) {
    if (page.originalIndex < 0 || page.originalIndex >= totalPages || seen.has(page.originalIndex)) {
      throw new Error('page_order_mismatch')
    }
    seen.add(page.originalIndex)
  }

  const outputPdf = await PDFDocument.create()

  for (let index = 0; index < order.length; index++) {
    const sourcePage = order[index]
    if (!sourcePage) {
      throw new Error('page_order_mismatch')
    }

    const [copiedPage] = await outputPdf.copyPages(sourcePdf, [sourcePage.originalIndex])
    outputPdf.addPage(copiedPage)
    onProgress?.({ current: index + 1, total: order.length })

    if (index % 5 === 4) {
      await new Promise((resolve) => window.setTimeout(resolve, 0))
    }
  }

  const out = await outputPdf.save()
  return {
    blob: new Blob([out], { type: 'application/pdf' }),
    totalPages,
    movedPages: countMovedPages(order),
  }
}
